import type { FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import type { UscCheckoutType } from "../../../shared-logic/types/UscCommonTypes";
import type { UscAccessoryType, UscPackType } from "../../utils/accessoryUtils";
import type { UscBasketReducerType } from "../reducers/UscBasketReducer";

export const USC_BASKET_SET_FINANCE_OPTION = "uscBasket/setFinanceOption";
export const USC_BASKET_SET_CHECKOUT_TYPE = "uscBasket/setCheckoutType";
export const USC_BASKET_ADD_ACCESSORY = "uscBasket/addAccessory";
export const USC_BASKET_REMOVE_ACCESSORY = "uscBasket/removeAccessory";
export const USC_BASKET_ADD_PACK = "uscBasket/addPack";
export const USC_BASKET_REMOVE_PACK = "uscBasket/removePack";
export const USC_BASKET_SET_CONFIGURATION = "uscBasket/setConfiguration";
export const USC_BASKET_SET_ACCESSORY_IDS = "uscBasket/setAccessoryIds";
export const USC_BASKET_SET_PACK_IDS = "uscBasket/setPackIds";

export type SetFinanceOptionType = {
    type: typeof USC_BASKET_SET_FINANCE_OPTION;
    option: FinanceOptionType;
    disableQueryUpdate: boolean; // OR-5042, this is used to prevent propagating the financeOption to query on init.
};

export const setFinanceOption = (
    option: FinanceOptionType,
    disableQueryUpdate: boolean = false,
): SetFinanceOptionType => ({
    type: USC_BASKET_SET_FINANCE_OPTION,
    option,
    disableQueryUpdate,
});

export type SetCheckoutTypeType = {
    type: typeof USC_BASKET_SET_CHECKOUT_TYPE;
    checkoutType: UscCheckoutType | null;
};

export const setCheckoutType = (checkoutType: UscCheckoutType | null): SetCheckoutTypeType => ({
    type: USC_BASKET_SET_CHECKOUT_TYPE,
    checkoutType,
});

export type AddAccessoryType = {
    type: typeof USC_BASKET_ADD_ACCESSORY;
    accessory: UscAccessoryType;
};

export const addAccessory = (accessory: UscAccessoryType): AddAccessoryType => ({
    type: USC_BASKET_ADD_ACCESSORY,
    accessory,
});

export type RemoveAccessoryType = {
    type: typeof USC_BASKET_REMOVE_ACCESSORY;
    id: string;
};

export const removeAccessory = (id: string): RemoveAccessoryType => ({
    type: USC_BASKET_REMOVE_ACCESSORY,
    id,
});

export type AddPackType = {
    type: typeof USC_BASKET_ADD_PACK;
    pack: UscPackType;
};

export const addPack = (pack: UscPackType): AddPackType => ({
    type: USC_BASKET_ADD_PACK,
    pack,
});

export type RemovePackType = {
    type: typeof USC_BASKET_REMOVE_PACK;
    id: string;
};

export const removePack = (id: string): RemovePackType => ({
    type: USC_BASKET_REMOVE_PACK,
    id,
});

export type SetConfigurationType = {
    type: typeof USC_BASKET_SET_CONFIGURATION;
    configuration: UscBasketReducerType["configuration"];
};

export const setConfiguration = (configuration: UscBasketReducerType["configuration"]): SetConfigurationType => ({
    type: USC_BASKET_SET_CONFIGURATION,
    configuration,
});

export type SetAccessoryIdsType = {
    type: typeof USC_BASKET_SET_ACCESSORY_IDS;
    accessoryIds: string[];
};

export const setAccessoryIds = (accessoryIds: string[]): SetAccessoryIdsType => ({
    type: USC_BASKET_SET_ACCESSORY_IDS,
    accessoryIds,
});

export type SetPackIdsType = {
    type: typeof USC_BASKET_SET_PACK_IDS;
    packIds: string[];
};

export const setPackIds = (packIds: string[]): SetPackIdsType => ({
    type: USC_BASKET_SET_PACK_IDS,
    packIds,
});

// Actions which should trigger basket logic.
// TODO: once services are added, split this up into
// accessoryActions, packActions (debatable), serviceActions
export const basketActions: UscBasketActionsType["type"][] = [
    USC_BASKET_ADD_ACCESSORY,
    USC_BASKET_REMOVE_ACCESSORY,
    USC_BASKET_ADD_PACK,
    USC_BASKET_REMOVE_PACK,
    USC_BASKET_SET_CONFIGURATION,
];

// Actions which should trigger a notification
export const basketNotificationActions: UscBasketNotificationActionsType["type"][] = [
    USC_BASKET_ADD_ACCESSORY,
    USC_BASKET_ADD_PACK,
];

export type UscBasketNotificationActionsType = AddAccessoryType | AddPackType;

export type UscBasketActionsType =
    | SetFinanceOptionType
    | SetCheckoutTypeType
    | AddAccessoryType
    | AddPackType
    | RemoveAccessoryType
    | RemovePackType
    | SetConfigurationType
    | SetAccessoryIdsType
    | SetPackIdsType;
